import { useCallback, useEffect, useState } from 'react';

interface UseInfiniteScrollOptions {
  hasMore: boolean | null;
  isLoading: boolean;
  onLoadMore: () => void;
  threshold?: number;
}

function getScrollableAncestor(el: HTMLElement): HTMLElement | null {
  let parent = el.parentElement;
  while (parent) {
    const { overflow, overflowY } = window.getComputedStyle(parent);
    const allowsScroll = /(auto|scroll)/.test(overflow + overflowY);
    const hasScrollableContent = parent.scrollHeight > parent.clientHeight;
    if (allowsScroll && hasScrollableContent) return parent;
    parent = parent.parentElement;
  }
  return null;
}

export function useInfiniteScroll({
  hasMore,
  isLoading,
  onLoadMore,
  threshold = 1,
}: UseInfiniteScrollOptions) {
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  // A callback ref, not a RefObject: the sentinel unmounts whenever a caller hides it (a scope
  // toggle, an empty state), and an effect keyed only on the loading flags would never re-observe
  // the remounted node.
  const [sentinel, setSentinel] = useState<HTMLDivElement | null>(null);
  const sentinelRef = useCallback((node: HTMLDivElement | null) => {
    setSentinel(node);
  }, []);

  useEffect(() => {
    if (!sentinel) return;

    const root = getScrollableAncestor(sentinel);

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && hasMore && !isLoading && !isLoadingMore) {
          setIsLoadingMore(true);
          onLoadMore();
        }
      },
      { threshold, root }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [sentinel, hasMore, isLoading, isLoadingMore, onLoadMore, threshold]);

  const reset = useCallback(() => setIsLoadingMore(false), []);

  return { sentinelRef, isLoadingMore, reset };
}
